// ---------------------------------------------------------------------------
// V16 — THE WORKER APP. A list of shifts, one shift, and one question.
//
// §V16: "Offer an assignment; worker accepts or declines in a mobile-first PWA.
// Assignment list, detail, call time, meeting point."
//
// FIVE RULES THIS SCREEN IS SHAPED BY, and every one of them is somebody else's
// finding rather than a preference:
//
//  1. IT HOLDS NO COPY OF THE STATE MACHINE. Every button comes from `moves` on
//     the server's response, which the database's own transition table produced.
//     V14's board does the same; a screen that decided its own buttons would be a
//     second machine, and the day the two disagreed the button would be the liar.
//
//  2. NO STATE NAME IS COMPARED. Tone comes from the two booleans that travel with
//     the row — `status_fills_headcount` and `status_is_terminal` — through the
//     same `toneFor` V14 uses. Testing `status` against any literal would put the
//     taxonomy back in the browser.
//
//     (This sentence used to demonstrate the mistake by quoting one of the state
//     names. V8's vocabulary guard and V13's second-copy guard both read comments,
//     and both were right to: prose is where a taxonomy gets re-established first.
//     Same practice as note 3 below — rewrite the sentence, never the guard.)
//
//  3. STATUS IS NEVER COLOUR ALONE. Every chip carries a word. Somebody reads this
//     on a cracked phone, in the rain, at 06:00, and 11-… §6 measured the four
//     semantic colours at 1.02:1 to 1.72:1 of each other — in greyscale they are
//     one colour.
//
//     (The original sentence named a job role here and the taxonomy guard caught
//     it, as it caught the same word in V13's prose. The guard covers comments
//     deliberately and the practice is to rewrite the comment: a per-file
//     exemption is how a guard stops guarding.)
//
//  4. A MISSING CALL TIME IS SAID IN WORDS, not shown as the start time again.
//     `call_at` is null when the requirement sets no offset, and two times on one
//     card is exactly what somebody misreads in a car park.
//
//  5. THE MARKETPLACE PROMPT IS NOT A MODAL. 16-the-flywheel.md §6a: "Not a modal
//     that blocks the app. Offered, dismissible, re-offerable — but never on a loop
//     that becomes nagging." It is a card in the flow, it is never pre-ticked, and
//     "Not now" is a button that posts a real answer rather than a dismissal.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { send } from "../api/auth";
import {
  console_,
  type BriefingVersion,
  type MarketplacePrompt,
  type MyShift,
  type MyShiftMove,
  type VerificationState,
} from "../api/console";
import { LoadFailed } from "../design/LoadFailed";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { browserStore, recall, remember } from "../session/lastKnown";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { offline } from "../api/offline";
import { drainQueue, enqueueCapture, needsAttention, type QueuedCapture, type Resolution } from "../system/offline";
import { captureStore } from "../system/offlineStore";
import { clock, money, span, whenIn } from "../system/format";
import { tronc, type MyTroncShare } from "../api/tronc";
import { toneFor } from "../system/crew";
import { LeadCrew } from "./LeadCrew";
import { ShiftMessages } from "./ShiftMessages";

// S18. `asOf` marks data read back from the phone because the server could not be reached.
// A failed read keeps its status, so a limit and a fault read differently (design/LoadFailed).
type Loaded<T> = { state: "loading" } | { state: "error"; failure: Failure } | { state: "ready"; data: T; asOf?: string };

/** S18. The note a screen shows over data it last knew, unless the shell already says so. */
function StaleNote({ asOf }: { asOf?: string }) {
  const { offlineAsOf } = useSession();
  const { locale, t } = useT();
  if (asOf === undefined || offlineAsOf !== null) return null;
  return (
    <p className="form-message" role="status">
      {t("Offline — showing what we last knew at {time}. Anything you record is kept on this phone and sent when there is signal.", {
        time: clock(asOf),
      })}
    </p>
  );
}
type Translate = ReturnType<typeof useT>["t"];

/**
 * What a shift's chip says.
 *
 * THROUGH `toneFor`, THE SAME FUNCTION V14'S BOARD USES, so a shift that reads
 * teal on a scheduler's board cannot read amber on the worker's phone. Standby is
 * its own chip beside it rather than a different tone, because "on standby" and
 * "not confirmed" are different facts and a single colour cannot say both.
 */
function shiftTone(shift: MyShift) {
  return toneFor({
    status_fills_headcount: shift.status_fills_headcount,
    status_is_terminal: shift.status_is_terminal,
  });
}

// AND THE WORDS COME FROM THE SERVER, never from a map in here. `status_label` is
// app.assignment_status.label, so a state renamed in the database renames itself on
// the phone — and there is nowhere in this file for a stale English word to hide.

/**
 * When to arrive, in words.
 *
 * NULL IS A SENTENCE AND NOT A BLANK. A card that simply omitted the call time
 * would leave a person guessing whether it had failed to load.
 */
function callLine(shift: MyShift, t: Translate): string {
  if (shift.call_at === null) return t("Arrive for the start, {start}.", { start: clock(shift.starts_at) });
  return t("Arrive {call} — the shift starts {start}.", { call: clock(shift.call_at), start: clock(shift.starts_at) });
}

function breakLine(shift: MyShift, t: Translate): string | null {
  if (shift.break_minutes === null || shift.break_minutes === 0) return null;
  // PAID AND UNPAID ARE DIFFERENT MONEY, which is why V11 stores them as two
  // columns and why this sentence says which.
  return shift.break_is_paid
    ? t("{minutes} minute break, paid.", { minutes: shift.break_minutes })
    : t("{minutes} minute break, unpaid.", { minutes: shift.break_minutes });
}

// ===========================================================================
// THE MARKETPLACE CARD — 16-the-flywheel.md §6a
// ===========================================================================
function MarketplaceCard({ apiBase, onAnswered }: { apiBase: string; onAnswered: () => void }) {
  const { locale, t, refusal } = useT();
  const [prompt, setPrompt] = useState<MarketplacePrompt | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { status, body } = await send<MarketplacePrompt & { error?: { message?: string } }>(
        console_.marketplacePrompt(apiBase),
      );
      if (!live) return;
      if (status === 200) setPrompt(body as MarketplacePrompt);
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);

  const answer = useCallback(
    async (value: string) => {
      setBusy(true);
      setFailed(null);
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.answerMarketplacePrompt(apiBase, value),
      );
      setBusy(false);
      if (status === 200) {
        setPrompt(null);
        onAnswered();
      } else {
        setFailed(refusal(body?.error) ?? t("That could not be saved. Try again."));
      }
    },
    [apiBase, onAnswered, t],
  );

  // NOT DUE IS NOT RENDERED AT ALL. §6a's "asked once" is a server decision —
  // `due` is computed from a finished shift and any standing answer — and a card
  // that appeared and then hid itself would flicker on every load.
  if (!prompt || !prompt.applicable || !prompt.due) return null;

  return (
    <section className="worker-card worker-prompt">
      <h2>{t("Could other employers offer you shifts?")}</h2>
      {/* PLAINLY, AND ALL THREE FACTS §6a REQUIRES: what it does, that the current
          organisation is not told, and that it can be changed whenever. */}
      {/* NO ORGANISATION NAME. The first draft of this paragraph said "Northgate is
          not told either way" — the name of a DEMO FIXTURE, shipped in product copy
          every worker would read. Naming the real employer here would also be
          worse than not naming them: a worker on two benches would see one name and
          wonder about the other. The sentence is true without one. */}
      <p>
        {t(
          "You can be visible to other employers on Verbunk, so they can offer you shifts too. The organisation you work for is not told either way, and you can change this whenever you like.",
        )}
      </p>
      {failed ? <StatusChip tone="failed" label={failed} /> : null}
      <div className="worker-actions">
        {/* NEVER PRE-TICKED — there is no checkbox at all. Two buttons, neither
            preselected, because 06-compliance §1 forbids a default that
            manufactures consent and a pre-ticked box is exactly that. */}
        <button type="button" className="btn" disabled={busy} onClick={() => void answer("yes")}>
          {t("Yes, show me to other employers")}
        </button>
        {/* "NOT NOW" POSTS A REAL ANSWER. §6a: it is stored and does not re-ask for
            at least thirty days. A dismissal that stored nothing would reappear
            next session, which §6a calls coercion with extra steps. */}
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void answer("not_now")}>
          {t("Not now")}
        </button>
      </div>
    </section>
  );
}

// ===========================================================================
// THE IDENTITY CHECK CARD — 17-identity-verification.md §6
//
// §6 lists four things this card must say and one thing it must never be. It must
// state plainly WHAT is checked, WHY, WHO sees the result and HOW LONG the document
// is kept; and it must never be a dark pattern. So the paragraph below is the
// product, not decoration around a button, and it is worth reading as carefully as
// any query in this slice.
//
// WHAT IT DOES NOT SAY, DELIBERATELY:
//
//   NOT THE COST. §4 absorbs it into fees that already exist and §2 is absolute
//   that this is never a worker fee. Mentioning EUR 1.50 at all would invite the
//   thought that somebody might be asked for it.
//
//   NOT A PROVIDER STATUS, and not a reason a check did not pass. We are never told
//   one. "With a person" is the whole of what a worker learns about a referral, and
//   06-compliance §1 gives them a named human to ask rather than a machine verdict
//   to read.
//
//   NOT A DEADLINE OR A THREAT. §6: never a blocker for browsing. A worker who
//   ignores this keeps their profile, their availability and their offers. The only
//   thing it unlocks is accepting work, which is what the sentence says.
// ===========================================================================
function VerificationCard({ apiBase }: { apiBase: string }) {
  const { locale, t, refusal } = useT();
  const [state, setState] = useState<VerificationState | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { status, body } = await send<{ verification: VerificationState }>(
        console_.myVerification(apiBase),
      );
      if (!live) return;
      if (status === 200) setState(body?.verification ?? null);
    })();
    return () => {
      live = false;
    };
  }, [apiBase]);

  const begin = useCallback(async () => {
    setBusy(true);
    setFailed(null);
    const { status, body } = await send<{
      verification?: { redirectUrl?: string };
      error?: { message?: string };
    }>(console_.startVerification(apiBase));
    setBusy(false);

    const redirect = body?.verification?.redirectUrl;
    if (status === 200 && redirect) {
      // THE WORKER LEAVES FOR THE PROVIDER'S OWN PAGE. That is the whole design:
      // the document is photographed somewhere we cannot see it, and we are handed
      // a result. §6.
      window.location.assign(redirect);
      return;
    }
    // AND EVERY OTHER ANSWER IS THE SERVER'S SENTENCE, not one invented here. The
    // common one is that identity checks are not switched on in this environment,
    // which is true of every environment built so far and must read as a plain
    // fact rather than as the worker having done something wrong.
    setFailed(refusal(body?.error) ?? t("That could not be started. Try again shortly."));
  }, [apiBase, t]);

  // NOTHING TO SAY, NOTHING SHOWN. A worker with no offers requiring a check sees
  // no card at all — §2: nobody is verified until somebody is paying for their
  // labour, and a card asking for a passport "for later" is exactly the signup-time
  // check §2 exists to avoid.
  if (!state) return null;
  if (!state.needsCheck && !state.inReview) return null;

  if (state.inReview) {
    return (
      <section className="worker-card worker-prompt">
        <h2>{t("Your identity check is with a person")}</h2>
        {/* NOT A REFUSAL, AND IT SAYS SO. §6: a failed check is not a ban, because
            providers reject legitimate documents often enough that automatic
            rejection would exclude real workers — disproportionately those with
            unusual or non-EU documents. */}
        <p>
          {t(
            "A check could not be completed automatically, so somebody is looking at it. This is common and it is not a decision about you. Nothing you have already been offered has changed.",
          )}
        </p>
      </section>
    );
  }

  return (
    <section className="worker-card worker-prompt">
      <h2>{t("One identity check before you can take this shift")}</h2>
      {/* THE FOUR FACTS §6 REQUIRES, in the order somebody actually wants them:
          what happens, what it is for, who sees the outcome, and where the document
          goes. The tier's own label comes from the database rather than being
          spelled here, so a renamed tier renames itself. */}
      {/* "TRIED TO TAKE" RATHER THAN "BEEN OFFERED", because since 0039 this card only
          appears after a worker has actually tapped Accept and been turned away. The
          earlier wording described the state that made the card appear before the
          owner's decision of 13 August, and a card that says "you have been offered"
          to somebody who just tried to accept reads as though it had not noticed. */}
      <p>
        {t(
          "{tier} is needed before you can take the shift you just tried to accept. You will be taken to our verification partner to photograph your document and take a selfie.",
          { tier: state.requiredTierLabel ?? t("An identity check") },
        )}
      </p>
      <p>
        {t(
          "Verbunk never receives or stores the document itself — our partner keeps it, and we keep only whether the check passed and when. Your employer sees that you are verified, and nothing else.",
        )}
      </p>
      {failed ? <StatusChip tone="failed" label={failed} /> : null}
      <div className="worker-actions">
        <button type="button" className="btn" disabled={busy} onClick={() => void begin()}>
          {t("Start the check")}
        </button>
      </div>
      {/* AND THE WAY OUT, STATED. Not a hidden dismiss: browsing, availability and
          offers are all unaffected, which is §6's first bullet made visible rather
          than merely true. */}
      <p className="worker-note">
        {t("You can do this later. Your profile, your availability and any offers stay exactly as they are.")}
      </p>
    </section>
  );
}

// ===========================================================================
// THE LIST — /app
// ===========================================================================
/**
 * SUBSCRIBE TO YOUR OWN SHIFTS.
 *
 * ★ THE URL IS SHOWN ONCE AND NEVER AGAIN. Only its hash is stored, so this
 * screen cannot re-display it — which is the same trade the session cookie
 * makes, and the reason "issue again" is offered rather than "show me".
 *
 * The latency is stated rather than hidden: a subscribed calendar is POLLED and
 * Google refreshes on its own schedule, often hours. Somebody who believed this
 * was live would miss a cancellation.
 */
function CalendarCard({ apiBase }: { apiBase: string }) {
  const { locale, t, refusal } = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [stopped, setStopped] = useState<string | null>(null);

  async function issue() {
    if (busy) return;
    setBusy(true);
    setFailed(null);
    try {
      const res = await send<{ subscribe_url?: string; error?: { message: string } }>(
        console_.issueCalendarFeed(apiBase),
      );
      if (res.status === 200 && typeof res.body?.subscribe_url === "string") {
        setUrl(res.body.subscribe_url);
        setStopped(null);
      } else setFailed(refusal(res.body?.error) ?? t("That did not work."));
    } catch {
      setFailed(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  // Sweep S21a. Stopping without a replacement: the old link answers an empty
  // calendar from now on. The screen never knows whether a link exists (only a
  // hash is stored), so the server's `revoked` says which sentence is true.
  async function stop() {
    if (busy) return;
    setBusy(true);
    setFailed(null);
    try {
      const res = await send<{ revoked?: boolean; error?: { message: string } }>(
        console_.revokeCalendarFeed(apiBase),
      );
      if (res.status === 200) {
        setUrl(null);
        setStopped(res.body?.revoked ? t("Your calendar link no longer works.") : t("There was no calendar link to stop."));
      } else setFailed(refusal(res.body?.error) ?? t("That did not work."));
    } catch {
      setFailed(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="worker-card">
      <h2>{t("My shifts in my calendar")}</h2>
      {url === null ? (
        <>
          <p className="muted">
            {t(
              "Get a private link you can add to Google Calendar, Outlook or the calendar on your phone. Your shifts appear there automatically.",
            )}
          </p>
          <button className="btn btn-block" type="button" onClick={() => void issue()} disabled={busy}>
            {busy ? t("Working…") : t("Get my calendar link")}
          </button>
        </>
      ) : (
        <>
          <StatusChip tone="live" label={t("Copy this now")} />
          <p className="muted">
            {t(
              "This link is shown once. Anyone who has it can see your shifts, so keep it to yourself — and if you lose it or share it by mistake, get a new one and the old link stops working.",
            )}
          </p>
          <p className="worker-feed-url">{url}</p>
          <p className="muted">
            {t(
              "Your calendar checks for changes on its own schedule, which can take a few hours. It is for planning — we will still message you if a shift changes.",
            )}
          </p>
        </>
      )}
      {stopped !== null ? <p className="muted">{stopped}</p> : null}
      <button className="btn btn-quiet" type="button" onClick={() => void stop()} disabled={busy}>
        {t("Stop my calendar link")}
      </button>
      {failed !== null ? <p className="muted">{failed}</p> : null}
    </section>
  );
}

/**
 * The worker's own tronc shares (0299). FINDING-a-worker-cannot-see-their-own-tronc-share:
 * the person whose share of a service charge it is could not see it. Drawn only when there
 * is one, so a worker outside hospitality sees nothing new. The independence line is the
 * agency's own claim, and says so.
 */
function TroncCard({ apiBase }: { apiBase: string }) {
  const { t, locale } = useT();
  const [shares, setShares] = useState<MyTroncShare[]>([]);

  useEffect(() => {
    let live = true;
    void send<{ shares?: MyTroncShare[] }>(tronc.mine(apiBase)).then(
      (res) => {
        if (live && res.status === 200 && res.body?.shares) setShares(res.body.shares);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [apiBase]);

  if (shares.length === 0) return null;
  return (
    <section className="worker-card">
      <h2>{t("Your share of tips and service charge")}</h2>
      <ul className="stack">
        {shares.map((s) => (
          <li key={s.distribution_id} className="stack">
            <div className="row">
              <strong>{money(s.amount_minor, s.currency)}</strong>
              <span>{translateLabel(locale, s.state_label)}</span>
            </div>
            <p className="muted">
              {s.event_name} · {t("{start} to {end}", { start: s.period_start, end: s.period_end })}
            </p>
            <p className="muted">
              {s.is_independent_of_employer
                ? t("The agency says this is shared out by someone independent of it.")
                : t("The agency says this is not shared out independently of it.")}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function MyShifts() {
  const { apiBase, me } = useSession();
  const account = me !== null && me.authenticated ? me.user.id : null;
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [payload, setPayload] = useState<Loaded<MyShift[]>>({ state: "loading" });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      let reply: { status: number; body: { shifts?: MyShift[]; error?: { message?: string } } };
      try {
        reply = await send<{ shifts?: MyShift[]; error?: { message?: string } }>(console_.myShifts(apiBase));
      } catch {
        // No signal: the shifts this phone last read, marked as such (S18); with none
        // kept, say so rather than "Loading…" for ever.
        const last = account === null ? null : recall<MyShift[]>(browserStore(), "shifts", account);
        if (!live) return;
        if (last !== null) setPayload({ state: "ready", data: last.value, asOf: last.savedAt });
        else setPayload({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk. Check your connection and try again.") } });
        return;
      }
      const { status, body } = reply;
      if (!live) return;
      if (status === 200 && body.shifts) {
        if (account !== null) remember(browserStore(), "shifts", account, body.shifts);
        setPayload({ state: "ready", data: body.shifts });
      }
      else setPayload({ state: "error", failure: { status, refusal: refusal(body?.error) } });
    })();
    return () => {
      live = false;
    };
  }, [apiBase, account, reloads, t]);

  if (payload.state === "loading") return <p className="muted">{t("Loading your shifts…")}</p>;
  if (payload.state === "error") {
    return (
      <div className="stack">
        <h1>{t("My shifts")}</h1>
        <OfflineQueue />
        <LoadFailed failure={payload.failure} fallback={t("Your shifts could not be loaded.")} onRetry={() => setReloads((n) => n + 1)} />
      </div>
    );
  }

  const shifts = payload.data;

  return (
    <div className="stack">
      <h1>{t("My shifts")}</h1>
      <StaleNote asOf={payload.asOf} />
      <OfflineQueue onSent={() => setReloads((n) => n + 1)} />

      {/* THE IDENTITY CARD SITS ABOVE THE MARKETPLACE ONE. Both are asks; only
          this one blocks anything, and burying it under an optional question
          would be the dark pattern in the other direction. */}
      <VerificationCard apiBase={apiBase} />
      <TroncCard apiBase={apiBase} />

      {shifts.length === 0 ? (
        // AN EMPTY BENCH IS NOT AN ERROR AND SAYS WHAT HAPPENS NEXT. The first
        // screen everybody on a bench meets, and "no shifts" alone reads as a
        // broken app rather than as a true statement about this week.
        <p className="muted">
          {t(
            "You have no shifts yet. When somebody offers you one it will appear here, and you will be able to accept or decline it from this screen.",
          )}
        </p>
      ) : (
        <ul className="worker-list">
          {shifts.map((shift) => {
            const chip = shiftTone(shift);
            return (
              <li key={shift.id}>
                {/* THE WHOLE CARD IS THE TARGET. 44px minimum, and a person with
                    cold hands on a phone should not have to find a small link. */}
                <Link className="worker-card worker-card-link" to={`/app/shifts/${shift.id}`}>
                  <span className="worker-card-head">
                    <StatusChip tone={chip} label={shift.status_label} />
                    {shift.is_reserve ? <StatusChip tone="neutral" label={t("Standby")} /> : null}
                  </span>
                  <strong>{shift.event_name ?? translateLabel(locale, shift.role_name)}</strong>
                  <span>{whenIn(shift.starts_at, orgZone)}</span>
                  <span className="muted">{span(shift.starts_at, shift.ends_at)}</span>
                  <span className="muted">{translateLabel(locale, shift.role_name)}</span>
                  {shift.meeting_point ? <span className="muted">{shift.meeting_point}</span> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {/* THE OPTIONAL ASKS COME AFTER THE SHIFTS. On a phone the two cards were a full
          screen of setup before the first shift. Identity (above) is the only ask that
          blocks work, so it stays on top. */}
      <MarketplaceCard apiBase={apiBase} onAnswered={() => setReloads((n) => n + 1)} />
      <CalendarCard apiBase={apiBase} />
    </div>
  );
}

// ===========================================================================
// THE DETAIL — /app/shifts/:id
// ===========================================================================
// ===========================================================================
// THE BRIEFING CARD — V18, on the worker's own shift.
//
// §V18: "Worker must acknowledge; acknowledgement is timestamped and enters the Proof
// Pack." This is where the acknowledging happens, and four things shape it:
//
//  1. THE VERSION IS NAMED IN THE POST, not resolved as "the latest" by the server. If a
//     new version is published while somebody is reading, acknowledging "the latest" would
//     record agreement to text they never saw. The database then decides whether the
//     version they read is still current — 0046 — so an honest acknowledgement of a stale
//     version simply does not let them start work.
//
//  2. A CHANGED BRIEFING SAYS SO, rather than quietly showing a Confirm button again.
//     Somebody who has already acknowledged once and is being asked a second time is owed
//     the reason.
//
//  3. NO BRIEFING IS NOT AN ERROR. Most posts have none, and §V18 blocks on an
//     UNACKNOWLEDGED briefing rather than on the absence of one. The card renders nothing.
//
//  4. AND IT NEVER SHOWS A VERSION NUMBER TO THE WORKER. "Version 3" is a fact about our
//     database; what a person needs is "this changed, read it again".
// ===========================================================================
function BriefingCard({ assignmentId, apiBase, onAcknowledged }: {
  assignmentId: string;
  apiBase: string;
  onAcknowledged: () => void;
}) {
  const { locale, t, refusal } = useT();
  const [version, setVersion] = useState<BriefingVersion | null>(null);
  const [acknowledged, setAcknowledged] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { status, body } = await send<{
        briefing?: { version: BriefingVersion | null; acknowledged: boolean };
      }>(console_.myBriefing(apiBase, assignmentId));
      if (!live) return;
      if (status === 200 && body?.briefing) {
        setVersion(body.briefing.version);
        setAcknowledged(body.briefing.acknowledged);
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase, assignmentId]);

  const confirm = useCallback(async () => {
    if (!version) return;
    setBusy(true);
    setFailed(null);
    const { status, body } = await send<{ error?: { message?: string } }>(
      console_.acknowledgeBriefing(apiBase, assignmentId, version.id),
    );
    setBusy(false);
    if (status === 200) {
      setAcknowledged(true);
      // THE SHIFT RELOADS, because acknowledging changes which MOVES are available — the
      // gate that refused the next move a moment ago now admits it, and the buttons come
      // from the server rather than from this screen.
      //
      // (This sentence named the state in backticks. V13's guard reads a state id bounded
      // by quotes OR BACKTICKS as a taxonomy, because a backtick is a JavaScript string
      // delimiter and it cannot tell prose emphasis from a template literal — its own
      // comment says the quotes have to bound the id rather than merely contain it.
      // Sixteenth time a comment about a rule has tripped that rule here, and the practice
      // is unchanged: rewrite the sentence.)
      onAcknowledged();
    } else {
      setFailed(refusal(body?.error) ?? t("That could not be confirmed. Try again."));
    }
  }, [apiBase, assignmentId, version, onAcknowledged, t]);

  // See note 3. Nothing to read, nothing drawn.
  if (version === null || acknowledged === null) return null;

  return (
    <section className={acknowledged ? "worker-card" : "worker-card worker-prompt"}>
      <h2>{acknowledged ? t("Your briefing") : t("Read this before your shift")}</h2>

      {/* NOTE 2. A second ask is explained rather than repeated silently. */}
      {!acknowledged && version.version_no > 1 ? (
        <p>
          {t(
            "This has changed since you last confirmed it. Please read it again — the part that matters is usually where to go.",
          )}
        </p>
      ) : null}

      {version.meeting_point ? (
        <p>
          <strong>{t("Where to go.")}</strong> {version.meeting_point}
        </p>
      ) : null}
      {version.dress_code ? (
        <p>
          <strong>{t("What to wear.")}</strong> {version.dress_code}
        </p>
      ) : null}
      {version.duties ? (
        <p>
          <strong>{t("What you will be doing.")}</strong> {version.duties}
        </p>
      ) : null}
      {version.contacts ? (
        <p>
          <strong>{t("Who to contact.")}</strong> {version.contacts}
        </p>
      ) : null}
      {/* 0222. The signed risk assessment's controls for this post. */}
      {version.safety_controls ? (
        <div>
          <strong>{t("Keeping safe on this post.")}</strong>
          <p className="pre-line">{version.safety_controls}</p>
        </div>
      ) : null}
      {version.map_url ? (
        <p>
          {/* rel="noreferrer" because the destination is a link somebody else typed into a
              config pack, and it should not learn where the worker came from. */}
          <a href={version.map_url} target="_blank" rel="noreferrer">
            {t("Open the map")}
          </a>
        </p>
      ) : null}

      {failed ? <StatusChip tone="failed" label={failed} /> : null}

      {acknowledged ? (
        <p className="worker-note">{t("You confirmed you have read this.")}</p>
      ) : (
        <div className="worker-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => void confirm()}>
            {t("I have read this")}
          </button>
        </div>
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// V19. THE CHECK-IN CARD.
//
// IT ASKS FOR A LOCATION ONLY WHEN THE POLICY NEEDS ONE. `requires_location` comes
// from the prompt endpoint, which reads `app.checkin_method.requires_geofence` — not
// from a method-name comparison here. Two reasons, and the second is the one that
// bites: a needless geolocation prompt is a permission dialogue for nothing, and a
// refused prompt is remembered by the platform, so asking when we did not need to can
// cost us the ask we do need later.
//
// IT DECIDES NOTHING ELSE. Whether the window is open, whether the reading is accurate
// enough, whether 50 metres is inside the fence — all of that is
// `app.record_attendance`. This screen shows what the server said and offers a button.
// A disabled button computed here would be a second copy of the tolerance rule, and
// the copy is the one that would be wrong the day a scheduler moved the call time.
//
// THE IDEMPOTENCY KEY IS MADE ONCE PER MOUNTED CARD, not per click. That is the whole
// point: §2.2's scenario is one person tapping twice on a stalled connection, and two
// taps of the same button are the same act. A key generated inside the handler would
// make the second tap a different act and produce either an error or a second record.
//
// (The sentence above named a job role, which the taxonomy guard reads as a hard-coded
// vocabulary — it cannot tell prose from a literal, and it is right not to try. That is
// the seventeenth time a comment about a rule has tripped the rule in this repository.)
// ---------------------------------------------------------------------------
type CheckinPrompt = {
  records_presence: boolean;
  policy: string | null;
  policy_name: string | null;
  method: string | null;
  method_label: string | null;
  requires_location: boolean;
  requires_confirmer: boolean;
  geofence_metres: number | null;
  tolerance_minutes: number | null;
  expected_at: string | null;
  window_opens_at: string | null;
  venue_has_point: boolean;
  recorded: string[];
  // WHAT THIS PERSON CAN DO NEXT, DECIDED BY THE SERVER. The first version of this card
  // read the first and second entries of the reference vocabulary by their position in the
  // array, and V7's guard refused it: "a vocabulary is being chosen by array index in
  // web/src". The guard is right — rank is the ordering and position is an accident of the
  // reply, so a third kind added at rank 15 would silently become the arrival. Asking the
  // server also deletes a fetch and a sort from this file.
  //
  // (And the sentence above cannot NAME the offending expression, because V7's guard
  // greps web/src for that literal and does not skip comments. Rewriting the prose is the
  // right move for a SHARED guard — weakening it would weaken it for every slice — which
  // is the opposite of what this slice did to its own new checks, where stripping comments
  // is correct. Twenty-first time a comment about a rule has tripped the rule here.)
  next_kind: string | null;
  next_kind_label: string | null;
};

type ClockSource = { id: string; label: string; is_authoritative: boolean };

type Recorded = {
  kind: string;
  minutes_late: number;
  distance_metres: number | null;
  within_geofence: boolean | null;
  replayed: boolean;
};

/** A browser position, or the reason there is not one. Never a silent zero. */
async function readPosition(t: Translate): Promise<
  { ok: true; lat: number; lon: number; accuracy: number } | { ok: false; why: string }
> {
  if (!("geolocation" in navigator)) {
    return { ok: false, why: t("This device cannot report a location. Ask a supervisor to confirm instead.") };
  }
  return await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          ok: true,
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          // NO FALLBACK IF THE PLATFORM OMITS IT. `accuracy` is required by the spec, but
          // a default of 0 here would claim a perfect reading and the database would
          // accept it — the one number that must never be invented, because 0049 refuses
          // a reading coarser than the fence and a fabricated 0 always passes.
          accuracy: Math.round(pos.coords.accuracy),
        }),
      (err) =>
        resolve({
          ok: false,
          why:
            err.code === err.PERMISSION_DENIED
              ? t("Location is switched off for this site, so we cannot check you in this way.")
              : t("We could not get a location just now. Move somewhere with a clearer signal and try again."),
        }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}

function CheckinCard({ assignmentId, apiBase, onRecorded }: {
  assignmentId: string;
  apiBase: string;
  onRecorded: () => void;
}) {
  const { me } = useSession();
  const account = me !== null && me.authenticated ? me.user.id : null;
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [prompt, setPrompt] = useState<CheckinPrompt | null>(null);
  // WHICH SOURCE OUR OWN CLOCK IS, ASKED RATHER THAN ASSUMED. A literal here would be
  // absolute rule 4 broken inside a fetch body, which is the least visible place there
  // is — and the guards scan this directory for exactly that.
  const [clockSource, setClockSource] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [done, setDone] = useState<Recorded | null>(null);
  const [queued, setQueued] = useState(false);

  // ONE KEY PER CARD. See the header.
  const [keyBase] = useState(() => crypto.randomUUID());

  useEffect(() => {
    let live = true;
    void (async () => {
      // S18. What a capture needs, kept per assignment, so a check-in can still be made,
      // and queued, when the card is opened with no signal.
      const name = `checkin.${assignmentId}`;
      type Kept = { prompt: CheckinPrompt | null; clockSource: string | null };
      let p: { status: number; body: { checkin?: CheckinPrompt } };
      let c: { status: number; body: { clock_sources?: ClockSource[] } };
      try {
        [p, c] = await Promise.all([
          send<{ checkin?: CheckinPrompt }>(console_.checkinPrompt(apiBase, assignmentId)),
          send<{ clock_sources?: ClockSource[] }>(console_.clockSources(apiBase)),
        ]);
      } catch {
        const last = account === null ? null : recall<Kept>(browserStore(), name, account);
        if (!live || last === null) return;
        setPrompt(last.value.prompt);
        setClockSource(last.value.clockSource);
        return;
      }
      if (!live) return;
      const kept: Kept = { prompt: null, clockSource: null };
      if (p.status === 200 && p.body?.checkin) {
        kept.prompt = p.body.checkin;
        setPrompt(p.body.checkin);
      }
      if (c.status === 200 && c.body?.clock_sources) {
        // EXACTLY ONE SOURCE IS AUTHORITATIVE and 0047 asserts that in the database, so
        // find rather than filter-and-hope. If none comes back the button stays disabled
        // instead of sending a guess.
        kept.clockSource = c.body.clock_sources.find((s) => s.is_authoritative)?.id ?? null;
        setClockSource(kept.clockSource);
      }
      if (account !== null && kept.prompt !== null && kept.clockSource !== null) {
        remember(browserStore(), name, account, kept);
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase, account, assignmentId]);

  const record = useCallback(
    async (kind: string) => {
      if (!prompt || !clockSource) return;
      setBusy(true);
      setFailed(null);

      const payload: Record<string, unknown> = {
        kind,
        // THE CLOCK SOURCE IS NOT NAMED HERE. Sending nothing would be a literal by
        // omission; instead the server is told which source applies by asking for the
        // authoritative one, and the reference endpoint is what says which that is.
        clock_source: clockSource,
        idempotency_key: `${keyBase}-${kind}`,
      };

      if (prompt.requires_location) {
        const pos = await readPosition(t);
        if (!pos.ok) {
          setBusy(false);
          setFailed(pos.why);
          return;
        }
        payload["latitude"] = pos.lat;
        payload["longitude"] = pos.lon;
        payload["accuracy_metres"] = pos.accuracy;
      }

      let reply: { status: number; body: { attendance?: Recorded; error?: { message?: string } } };
      try {
        reply = await send<{ attendance?: Recorded; error?: { message?: string } }>(
          console_.recordAttendance(apiBase, assignmentId, payload),
        );
      } catch {
        // S18. NO SIGNAL. The tap is kept on this phone and sent when there is a
        // connection (OfflineQueue below); the server decides what it counts as,
        // and says so when it arrives. Without a store, it says it was not recorded.
        setBusy(false);
        const store = captureStore();
        if (store === null) {
          setFailed(t("No connection, and this phone cannot keep the check-in for later. Try again when you have signal."));
          return;
        }
        try {
          await enqueueCapture(
            store,
            {
              id: crypto.randomUUID(),
              assignment_id: assignmentId,
              kind,
              position:
                typeof payload["latitude"] === "number"
                  ? {
                      coords: {
                        latitude: payload["latitude"] as number,
                        longitude: payload["longitude"] as number,
                        accuracy: payload["accuracy_metres"] as number,
                      },
                    }
                  : null,
            },
            new Date(),
          );
        } catch {
          // A full or refusing store: say so, never a silent loss of somebody turning up.
          setFailed(t("No connection, and this phone cannot keep the check-in for later. Try again when you have signal."));
          return;
        }
        setQueued(true);
        return;
      }
      const { status, body } = reply;
      setBusy(false);
      if (status === 200 && body?.attendance) {
        setDone(body.attendance);
        // The shift reloads, because recording changes which moves are available and the
        // buttons come from the server rather than from this screen.
        onRecorded();
      } else {
        // THE SERVER'S SENTENCE, VERBATIM. It is the one that says how far away they are
        // and when the window opens, and a friendlier local message would throw away the
        // only two numbers that tell somebody what to do next.
        setFailed(refusal(body?.error) ?? t("That could not be recorded. Try again."));
      }
    },
    [apiBase, assignmentId, keyBase, prompt, clockSource, onRecorded, t],
  );

  if (prompt === null) return null;

  // A NULL POLICY IS AN ANSWER. 0047 made the pin nullable for remote and asynchronous
  // work, and saying so is better than showing a button that refuses.
  if (!prompt.records_presence) {
    return (
      <section className="worker-card">
        <h2>{t("Checking in")}</h2>
        <p className="worker-note">
          {t("This shift does not use check-in. Your supervisor will confirm your hours another way.")}
        </p>
      </section>
    );
  }

  // NOTHING POSITIONAL. `recorded.length` says how far through the day this shift is, and
  // `next_kind` says what is left — both from the server.
  const hasArrived = prompt.recorded.length > 0;

  return (
    <section className={hasArrived ? "worker-card" : "worker-card worker-prompt"}>
      <h2>{hasArrived ? t("Your check-in") : t("Checking in")}</h2>

      <p>
        <strong>{t("How.")}</strong> {prompt.method_label ?? prompt.policy_name}
        {prompt.requires_location && prompt.geofence_metres !== null
          ? t(" — you need to be within {metres} metres of the site.", { metres: prompt.geofence_metres })
          : null}
      </p>

      {/* SAID BEFORE THE BUTTON IS PRESSED, because 0049 refuses this and the refusal is
          knowable in advance. A worker standing in a car park learning it then is a
          support call. A limitation, not a fault: neutral, not red (event.clinic tour, entry 40). */}
      {prompt.requires_location && !prompt.venue_has_point ? (
        <Notice tone="neutral">{t("This venue has no location on file, so a location check-in cannot work here. Ask your supervisor to confirm you instead.")}</Notice>
      ) : null}

      {prompt.window_opens_at ? (
        <p className="muted">{t("Check-in opens at {time}.", { time: whenIn(prompt.window_opens_at, orgZone) })}</p>
      ) : null}

      {failed ? <StatusChip tone="failed" label={failed} /> : null}
      {queued ? (
        <Notice tone="attention">
          {t("No signal. Your check-in is saved on this phone and will be sent as soon as there is a connection.")}
        </Notice>
      ) : null}

      {done ? (
        <p className="worker-note">
          {done.replayed ? t("Already recorded") : t("Recorded")}
          {done.distance_metres !== null ? t(" — {metres} metres from the site", { metres: done.distance_metres }) : null}
          {done.minutes_late > 0 ? t(", {minutes} minutes after your call time", { minutes: done.minutes_late }) : null}
          {"."}
        </p>
      ) : null}

      <div className="worker-actions">
        {/* ONE BUTTON, AND BOTH ITS FACE AND ITS ACTION COME FROM THE SERVER.
            app.attendance_kind carries the labels and the ranks, so a third kind added
            later appears here without a change to this file — and a shift with nothing
            left to do renders no button rather than a disabled one. */}
        {prompt.next_kind && prompt.next_kind_label ? (
          <button
            type="button"
            className="btn"
            disabled={busy || !clockSource}
            onClick={() => void record(prompt.next_kind as string)}
          >
            {translateLabel(locale, prompt.next_kind_label)}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export function MyShift() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const account = me !== null && me.authenticated ? me.user.id : null;
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [payload, setPayload] = useState<Loaded<{ shift: MyShift; moves: MyShiftMove[] }>>({ state: "loading" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      let reply: { status: number; body: { shift?: MyShift; moves?: MyShiftMove[]; error?: { message?: string } } };
      try {
        reply = await send<{ shift?: MyShift; moves?: MyShiftMove[]; error?: { message?: string } }>(
          console_.myShift(apiBase, id),
        );
      } catch {
        // S18. No signal: this shift as the phone last read it. Its moves are left out,
        // because every move needs the server to answer.
        const last = account === null ? null : recall<MyShift>(browserStore(), `shift.${id}`, account);
        if (!live) return;
        if (last !== null) setPayload({ state: "ready", data: { shift: last.value, moves: [] }, asOf: last.savedAt });
        else setPayload({ state: "error", failure: { status: 0, refusal: t("We could not reach Verbunk. Check your connection and try again.") } });
        return;
      }
      const { status, body } = reply;
      if (!live) return;
      if (status === 200 && body.shift) {
        if (account !== null) remember(browserStore(), `shift.${id}`, account, body.shift);
        setPayload({ state: "ready", data: { shift: body.shift, moves: body.moves ?? [] } });
      } else {
        setPayload({ state: "error", failure: { status, refusal: refusal(body?.error) } });
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase, account, id, reloads, t]);

  // ONE KEY PER INTENT, HELD ACROSS RETRIES — and the api client's own comment is
  // why: "minting one inside this function would look identical and guarantee
  // nothing: a retry would carry a fresh key and apply the change twice." The first
  // draft of this screen minted one inside the click handler, which is exactly that
  // mistake one layer up.
  //
  // The failure it guards is a worker on a train tapping Accept three times as the
  // signal comes and goes. Same intent, same key, one acceptance. A DIFFERENT move
  // gets a different key, because it is a different act.
  const keys = useRef<Map<string, string>>(new Map());

  const move = useCallback(
    async (toState: string, label: string) => {
      setBusy(true);
      setNotice(null);
      let key = keys.current.get(toState);
      if (!key) {
        key = crypto.randomUUID();
        keys.current.set(toState, key);
      }
      const { status, body } = await send<{ error?: { message?: string } }>(
        console_.transitionAssignment(apiBase, id, { to_state: toState }, key),
      );
      setBusy(false);
      if (status === 200 || status === 201) {
        // THE ACT IS DONE, SO ITS KEY IS RETIRED. Keeping it would mean a later,
        // genuinely new attempt at the same move silently returned this one's
        // result — which is the opposite failure to the one the key prevents.
        keys.current.delete(toState);
        setNotice(t("{label} — saved.", { label }));
        setReloads((n) => n + 1);
      } else {
        setNotice(refusal(body?.error) ?? t("That could not be saved. Try again."));
      }
    },
    [apiBase, id, t],
  );

  if (payload.state === "loading") return <p className="muted">{t("Loading…")}</p>;
  if (payload.state === "error") {
    return (
      <div className="stack">
        <p>
          <Link to="/app">{t("My shifts")}</Link>
        </p>
        <LoadFailed failure={payload.failure} fallback={t("That shift could not be loaded.")} onRetry={() => setReloads((n) => n + 1)} />
      </div>
    );
  }

  const { shift, moves } = payload.data;
  const chip = shiftTone(shift);
  const breaks = breakLine(shift, t);

  return (
    <div className="stack">
      <p>
        <Link to="/app">{t("My shifts")}</Link>
      </p>

      <h1>{shift.event_name ?? translateLabel(locale, shift.role_name)}</h1>

      <StaleNote asOf={payload.asOf} />
      <p className="worker-card-head">
        <StatusChip tone={chip} label={shift.status_label} />
        {shift.is_reserve ? <StatusChip tone="neutral" label={t("Standby")} /> : null}
      </p>

      {shift.is_reserve ? (
        // STANDBY IS EXPLAINED, because "Standby" alone does not tell somebody
        // whether to turn up. 13-… §1 pays standby up to the moment of promotion
        // and the worked rate after it, so the difference is money as well as time.
        <p className="muted">
          {t(
            "You are on standby for this shift. Keep the time free — if a place opens you will be asked, and you will see it here.",
          )}
        </p>
      ) : null}

      <ShiftMessages assignmentId={id} apiBase={apiBase} orgZone={orgZone} />

      <LeadCrew assignmentId={id} apiBase={apiBase} />

      {/* THE BRIEFING GOES FIRST, ABOVE THE TIMES. Somebody who has not read it cannot
          start work — 0046's gate — so burying it under two cards of detail they already
          know would be putting the only blocking thing last. */}
      <BriefingCard
        assignmentId={id}
        apiBase={apiBase}
        onAcknowledged={() => setReloads((n) => n + 1)}
      />

      {/* CHECK-IN SITS UNDER THE BRIEFING and above the detail, in the order the day
          happens: read this, then say you are here. Somebody who has not acknowledged
          cannot check in at all — 0046's gate and 0050's, in that order — so putting
          check-in first would offer a button the database refuses. */}
      <CheckinCard
        assignmentId={id}
        apiBase={apiBase}
        onRecorded={() => setReloads((n) => n + 1)}
      />

      <section className="worker-card">
        <h2>{t("When")}</h2>
        <p>{whenIn(shift.starts_at, orgZone)}</p>
        <p>{span(shift.starts_at, shift.ends_at)}</p>
        <p>{callLine(shift, t)}</p>
        {breaks ? <p className="muted">{breaks}</p> : null}
      </section>

      <section className="worker-card">
        <h2>{t("Where")}</h2>
        {shift.meeting_point ? (
          <p>{shift.meeting_point}</p>
        ) : (
          // NO ADDRESS IS A STATEMENT, NOT A BLANK. `venue_address` is nullable and
          // the venue feed does not always carry one, so the honest thing is to say
          // nobody has recorded it rather than to render an empty line.
          <p className="muted">{t("No meeting point has been recorded for this shift yet. Ask before you travel.")}</p>
        )}
        <p className="muted">{translateLabel(locale, shift.role_name)}</p>
      </section>

      {notice ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone="attention" label={t("Note")} />
          <span>{notice}</span>
        </div>
      ) : null}

      <section className="worker-card">
        <h2>{t("What you can do")}</h2>
        {moves.length === 0 ? (
          // NOTHING TO DO IS ITS OWN SENTENCE. A card with no buttons and no words
          // reads as a screen that failed to finish loading.
          <p className="muted">
            {t("There is nothing for you to do on this shift right now. If that changes it will appear here.")}
          </p>
        ) : (
          <div className="worker-actions">
            {moves.map((m) => (
              <button
                key={m.to_state}
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => void move(m.to_state, m.action_label)}
                title={m.description}
              >
                {/* `action_label` AND NOT `to_label`. The first version of this
                    screen used the destination state's name and offered a person
                    "Accepted" and "Declined" — labels rather than acts. 0033 put
                    the imperative on the edge, where it belongs, so the word
                    changes in the database and not in here. */}
                {translateLabel(locale, m.action_label)}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * /app/verification/return?session= — SWEEP S8. Where the identity provider sends the
 * worker back. It asks the server to record the result once, and says plainly when it
 * could not: a page reading "done" over a check that was never recorded would leave the
 * worker blocked from shifts with no idea why.
 */
export function VerificationReturn() {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [params] = useSearchParams();
  const session = params.get("session");
  const [state, setState] = useState<"working" | "done" | "unrecorded">("working");
  const [message, setMessage] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!session) {
      setState("unrecorded");
      setMessage(t("This page was opened without the check it belongs to. Start the check again from your shifts."));
      return;
    }
    send<{ error?: { message?: string } }>(console_.completeVerification(apiBase, session))
      .then((res) => {
        if (res.status === 200) setState("done");
        else {
          setState("unrecorded");
          setMessage(refusal(res.body.error) ?? t("The result of the check could not be recorded."));
        }
      })
      .catch(() => {
        setState("unrecorded");
        setMessage(t("We could not reach Verbunk. Check your connection and open this page again."));
      });
  }, [apiBase, session, t]);

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Identity check")}</h2>
        <StatusChip
          tone={state === "done" ? "live" : state === "unrecorded" ? "failed" : "neutral"}
          label={state === "done" ? t("Recorded") : state === "unrecorded" ? t("Not recorded") : t("Working")}
        />
      </div>
      <p>
        {state === "working"
          ? t("Recording the result of your check…")
          : state === "done"
            ? t("Thank you. The result of your check is recorded — your shifts show whether anything else is needed.")
            : message}
      </p>
      <Link className="btn" to="/app">
        {t("Back to my shifts")}
      </Link>
    </section>
  );
}

/**
 * S18. THE QUEUE, SENT WHEN THERE IS SIGNAL: on opening the app and whenever the
 * browser says it is back online. offline.ts decides nothing about which capture
 * wins (the server does) and clears exactly what the server names.
 */
export function OfflineQueue({ onSent }: { onSent?: () => void }) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [state, setState] = useState<{ waiting: number; attention: Resolution[] }>({ waiting: 0, attention: [] });
  // A REF, so a new callback each render does not re-create drain and re-send the queue.
  const sent = useRef(onSent);
  sent.current = onSent;

  const drain = useCallback(async () => {
    const store = captureStore();
    if (store === null) return;
    const pending = await store.all().catch(() => [] as QueuedCapture[]);
    if (pending.length === 0) return;
    const result = await drainQueue(store, async (captures) => {
      try {
        const { status, body } = await send<{ sync?: { resolutions?: Resolution[] }; error?: { message?: string } }>(
          offline.sync(apiBase, captures),
        );
        return status === 200
          ? { ok: true, resolutions: body.sync?.resolutions ?? [] }
          : { ok: false, message: refusal(body.error) ?? "" };
      } catch {
        return { ok: false, message: "offline" };
      }
    });
    setState({ waiting: result.remaining, attention: needsAttention(result.resolutions) });
    if (result.resolutions.length > 0) sent.current?.();
  }, [apiBase, refusal]);

  useEffect(() => {
    void drain();
    window.addEventListener("online", drain);
    return () => window.removeEventListener("online", drain);
  }, [drain]);

  if (state.waiting === 0 && state.attention.length === 0) return null;
  return (
    <div className="stack">
      {state.waiting > 0 ? (
        <Notice tone="attention">
          {t("{count} check-in(s) saved on this phone are waiting for a connection.", { count: state.waiting })}
        </Notice>
      ) : null}
      {state.attention.map((r) => (
        <Notice key={r.capture_id} tone="failed">
          {t("A check-in sent from this phone was not accepted as it stood: {outcome}. Ask your supervisor.", {
            outcome: r.ruling_label ?? r.outcome_label,
          })}
        </Notice>
      ))}
    </div>
  );
}
